import { useCallback, useMemo, useState } from 'react';

import { isDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import type { FileCategory } from '../types/file-category.type';
import type { FileItem } from '../types/file-item.type';

interface Params {
  readonly allowedFileCategories: readonly FileCategory[];
  readonly files: readonly FileItem[];
  readonly previewFileId?: Id | null;
  readonly selectedFileCategoryId: Id;
  readonly onDeleteFiles: (fileIds: readonly Id[]) => Promise<void>;
  readonly onMoveFiles: (
    params: Readonly<{
      fileIds: readonly Id[];
      destinationFileCategoryId: Id;
    }>,
  ) => Promise<void>;
  readonly onPreviewChange?: (fileId: Id | null) => void;
  readonly onRenameFile: (
    params: Readonly<{ fileId: Id; newOriginalName: string }>,
  ) => Promise<void>;
}

// Shared by every FileExplorer view (table, grid, ...): dialog state and mutation wiring for
// delete/move/rename/preview are identical no matter how files are laid out on screen. Row/card
// selection is intentionally left out - it's tied to each view's own selection widget.
export const useFilesActions = ({
  allowedFileCategories,
  files,
  previewFileId,
  selectedFileCategoryId,
  onDeleteFiles,
  onMoveFiles,
  onPreviewChange,
  onRenameFile,
}: Readonly<Params>) => {
  const [localPreviewFile, setLocalPreviewFile] = useState<FileItem | null>(
    null,
  );
  const [pendingDeleteFileIds, setPendingDeleteFileIds] = useState<
    readonly Id[]
  >([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pendingMoveFileIds, setPendingMoveFileIds] = useState<readonly Id[]>(
    [],
  );
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [fileToRename, setFileToRename] = useState<FileItem | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [previousSelectedFileCategoryId, setPreviousSelectedFileCategoryId] =
    useState(selectedFileCategoryId);

  // Compressed versions and thumbnails are technical files: they never get their own row/card,
  // but their existence/size feeds the "compressed" / "thumbnail" indicators on the original
  // file - so the full list (including hidden ones) is kept around for that lookup.
  const visibleFiles = useMemo(
    () => files.filter((file) => !file.hideInFileExplorer),
    [files],
  );

  const filesById = useMemo(
    () => new Map(files.map((file) => [file._id, file])),
    [files],
  );

  const isControlledPreview = isDefined(onPreviewChange);

  const previewFile: FileItem | null = isControlledPreview
    ? isDefined(previewFileId)
      ? (visibleFiles.find((f) => f._id === previewFileId) ?? null)
      : null
    : localPreviewFile;

  if (
    selectedFileCategoryId !== previousSelectedFileCategoryId &&
    !isControlledPreview
  ) {
    setPreviousSelectedFileCategoryId(selectedFileCategoryId);
    setLocalPreviewFile(null);
  }

  const destinationFileCategories = useMemo(
    () =>
      allowedFileCategories.filter(
        (category) => category._id !== selectedFileCategoryId,
      ),
    [allowedFileCategories, selectedFileCategoryId],
  );

  // --- Delete ---

  const openDeleteDialog = useCallback((targetFileIds: readonly Id[]) => {
    setDeleteError(null);
    setPendingDeleteFileIds(targetFileIds);
    setIsDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, []);

  // Takes an `onSuccess` callback instead of returning a promise: the caller's own
  // success-only concerns (clearing a row/card selection) shouldn't have to duplicate this
  // hook's error handling to avoid an unhandled rejection.
  const confirmDelete = useCallback(
    (onSuccess?: () => void) => {
      onDeleteFiles(pendingDeleteFileIds)
        .then(() => {
          setIsDeleteDialogOpen(false);
          onSuccess?.();
        })
        .catch((deleteFilesError: unknown) => {
          setDeleteError(
            deleteFilesError instanceof Error
              ? deleteFilesError.message
              : 'Une erreur est survenue.',
          );
        });
    },
    [onDeleteFiles, pendingDeleteFileIds],
  );

  // --- Move ---

  const openMoveDialog = useCallback((targetFileIds: readonly Id[]) => {
    setMoveError(null);
    setPendingMoveFileIds(targetFileIds);
    setIsMoveDialogOpen(true);
  }, []);

  const closeMoveDialog = useCallback(() => {
    setIsMoveDialogOpen(false);
  }, []);

  const confirmMove = useCallback(
    (destinationFileCategoryId: Id, onSuccess?: () => void) => {
      onMoveFiles({ fileIds: pendingMoveFileIds, destinationFileCategoryId })
        .then(() => {
          setIsMoveDialogOpen(false);
          onSuccess?.();
        })
        .catch((moveFilesError: unknown) => {
          setMoveError(
            moveFilesError instanceof Error
              ? moveFilesError.message
              : 'Une erreur est survenue.',
          );
        });
    },
    [onMoveFiles, pendingMoveFileIds],
  );

  // --- Rename ---

  const openRenameDialog = useCallback((file: FileItem) => {
    setRenameError(null);
    setFileToRename(file);
  }, []);

  const closeRenameDialog = useCallback(() => {
    setFileToRename(null);
  }, []);

  const confirmRename = useCallback(
    (newOriginalName: string) => {
      if (!fileToRename) {
        return;
      }
      onRenameFile({ fileId: fileToRename._id, newOriginalName })
        .then(() => {
          setFileToRename(null);
        })
        .catch((renameFileError: unknown) => {
          setRenameError(
            renameFileError instanceof Error
              ? renameFileError.message
              : 'Une erreur est survenue.',
          );
        });
    },
    [fileToRename, onRenameFile],
  );

  // --- Preview ---

  const openPreview = useCallback(
    (file: FileItem) => {
      if (isControlledPreview) {
        onPreviewChange(file._id);
      } else {
        setLocalPreviewFile(file);
      }
    },
    [isControlledPreview, onPreviewChange],
  );

  const closePreview = useCallback(() => {
    if (isControlledPreview) {
      onPreviewChange(null);
    } else {
      setLocalPreviewFile(null);
    }
  }, [isControlledPreview, onPreviewChange]);

  const deletePreviewedFile = useCallback(
    (fileId: Id) => onDeleteFiles([fileId]),
    [onDeleteFiles],
  );

  const movePreviewedFile = useCallback(
    ({
      fileId,
      destinationFileCategoryId,
    }: Readonly<{ fileId: Id; destinationFileCategoryId: Id }>) =>
      onMoveFiles({ fileIds: [fileId], destinationFileCategoryId }),
    [onMoveFiles],
  );

  return {
    closeDeleteDialog,
    closeMoveDialog,
    closePreview,
    closeRenameDialog,
    confirmDelete,
    confirmMove,
    confirmRename,
    deleteError,
    deletePreviewedFile,
    destinationFileCategories,
    fileToRename,
    filesById,
    isDeleteDialogOpen,
    isMoveDialogOpen,
    movePreviewedFile,
    moveError,
    openDeleteDialog,
    openMoveDialog,
    openPreview,
    openRenameDialog,
    pendingDeleteFileIds,
    pendingMoveFileIds,
    previewFile,
    renameError,
    renamePreviewedFile: onRenameFile,
    visibleFiles,
  };
};
